import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Navigation, 
  Plus, 
  MapPin, 
  Truck,
  Users,
  Clock,
  Fuel,
  Route,
  Brain,
  Loader2,
  Trash2,
  Send,
  Target,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const priorityOptions = [
  { value: "low", label: "Low", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  { value: "normal", label: "Normal", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "high", label: "High", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "urgent", label: "Urgent", className: "bg-red-500/20 text-red-400 border-red-500/30" }
];

const statusOptions = [
  { value: "planned", label: "Planned", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  { value: "in_progress", label: "In Progress", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "completed", label: "Completed", className: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "cancelled", label: "Cancelled", className: "bg-red-500/20 text-red-400 border-red-500/30" }
];

// Custom destination marker
const createDestinationIcon = (index) => {
  return L.divIcon({
    className: "custom-div-icon",
    html: `
      <div style="
        background: #4ade80;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 3px solid #0a0c0a;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 15px rgba(74, 222, 128, 0.6);
        font-weight: bold;
        color: #0a0c0a;
        font-size: 12px;
      ">${index + 1}</div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

// Tanzania key locations for quick selection
const TANZANIA_LOCATIONS = [
  { name: "Dar es Salaam HQ", lat: -6.7924, lng: 39.2083 },
  { name: "Lugalo Military Base", lat: -6.8500, lng: 39.2500 },
  { name: "Julius Nyerere Airport", lat: -6.8781, lng: 39.2026 },
  { name: "Dodoma (Capital)", lat: -6.1630, lng: 35.7516 },
  { name: "Arusha", lat: -3.3869, lng: 36.6830 },
  { name: "Mwanza", lat: -2.5164, lng: 32.9175 },
  { name: "Mbeya", lat: -8.9000, lng: 33.4500 },
  { name: "Tanga", lat: -5.0689, lng: 39.0989 },
];

const MapController = ({ destinations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (destinations.length > 0) {
      const bounds = L.latLngBounds(
        destinations.map(d => [d.latitude, d.longitude])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [destinations, map]);
  
  return null;
};

export default function MissionPlannerPage() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState("");
  const [routeInfo, setRouteInfo] = useState(null);
  
  const [destinations, setDestinations] = useState([]);
  const [newDestination, setNewDestination] = useState({ name: "", latitude: "", longitude: "", notes: "" });
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    scheduled_date: new Date().toISOString().split("T")[0],
    scheduled_time: "08:00",
    priority: "normal",
    vehicle_requirements: "",
    organization_id: ""
  });

  const canEdit = user?.role === "admin" || user?.role === "fleet_manager";
  const defaultCenter = [-6.7924, 39.2083];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [missionsRes, vehiclesRes, driversRes, orgsRes] = await Promise.all([
        apiClient.get("/missions"),
        apiClient.get("/vehicles"),
        apiClient.get("/drivers"),
        apiClient.get("/organizations")
      ]);
      setMissions(missionsRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
      setOrganizations(orgsRes.data);
      
      if (orgsRes.data.length > 0 && !formData.organization_id) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data[0].id }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const addDestination = () => {
    if (!newDestination.name || !newDestination.latitude || !newDestination.longitude) {
      toast.error("Please fill in destination name and coordinates");
      return;
    }
    
    setDestinations([...destinations, {
      name: newDestination.name,
      latitude: parseFloat(newDestination.latitude),
      longitude: parseFloat(newDestination.longitude),
      notes: newDestination.notes
    }]);
    setNewDestination({ name: "", latitude: "", longitude: "", notes: "" });
  };

  const addQuickLocation = (location) => {
    setDestinations([...destinations, {
      name: location.name,
      latitude: location.lat,
      longitude: location.lng,
      notes: ""
    }]);
  };

  const removeDestination = (index) => {
    setDestinations(destinations.filter((_, i) => i !== index));
  };

  const handleAIPlan = async () => {
    if (destinations.length === 0) {
      toast.error("Please add at least one destination");
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await apiClient.post("/ai/plan-mission", {
        mission_description: formData.description || formData.title,
        destinations: destinations,
        time_constraints: formData.scheduled_time ? `Departure at ${formData.scheduled_time}` : null
      });
      setAiRecommendation(response.data.recommendation);
      toast.success("AI recommendation generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get AI recommendation");
    } finally {
      setAiLoading(false);
    }
  };

  const handleCalculateRoute = async () => {
    if (destinations.length < 2) {
      toast.error("Please add at least 2 destinations for route calculation");
      return;
    }
    
    setRouteLoading(true);
    try {
      const waypoints = destinations.slice(1, -1).map(d => ({ lat: d.latitude, lng: d.longitude }));
      
      const response = await apiClient.post("/routes/calculate", {
        start_lat: destinations[0].latitude,
        start_lng: destinations[0].longitude,
        end_lat: destinations[destinations.length - 1].latitude,
        end_lng: destinations[destinations.length - 1].longitude,
        waypoints: waypoints.length > 0 ? waypoints : null
      });
      setRouteInfo(response.data);
      toast.success("Route calculated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate route");
    } finally {
      setRouteLoading(false);
    }
  };

  const handleOptimizeRoute = async () => {
    if (destinations.length < 2) {
      toast.error("Please add at least 2 destinations for optimization");
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await apiClient.post("/ai/optimize-route", destinations);
      setAiRecommendation(response.data.optimization_suggestion);
      toast.success("Route optimization suggestion generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to optimize route");
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateMission = async () => {
    if (!formData.title || destinations.length === 0) {
      toast.error("Please provide a title and at least one destination");
      return;
    }
    
    try {
      const missionData = {
        ...formData,
        destinations: destinations
      };
      
      const response = await apiClient.post("/missions", missionData);
      
      // Update with AI recommendation and route info if available
      if (aiRecommendation || routeInfo) {
        await apiClient.put(`/missions/${response.data.id}`, {
          ai_recommendation: aiRecommendation,
          route_info: routeInfo
        });
      }
      
      toast.success("Mission created successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create mission");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      scheduled_date: new Date().toISOString().split("T")[0],
      scheduled_time: "08:00",
      priority: "normal",
      vehicle_requirements: "",
      organization_id: organizations[0]?.id || ""
    });
    setDestinations([]);
    setAiRecommendation("");
    setRouteInfo(null);
    setNewDestination({ name: "", latitude: "", longitude: "", notes: "" });
  };

  const getStatusBadge = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const getPriorityBadge = (priority) => {
    return priorityOptions.find(p => p.value === priority) || priorityOptions[1];
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="mission-planner-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mission-planner-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            MISSION PLANNER
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            AI-powered dispatch and route optimization
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="new-mission-btn">
                <Plus className="w-4 h-4 mr-2" />
                NEW MISSION
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm max-w-4xl max-h-[90vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-primary" />
                  PLAN NEW MISSION
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-y-auto max-h-[70vh] pr-2">
                {/* Left Column - Mission Details */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Mission Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Supply delivery to Dodoma"
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      data-testid="mission-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the mission objectives..."
                      className="bg-input border-border rounded-sm font-mono text-sm min-h-[60px]"
                      data-testid="mission-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Date</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        className="bg-input border-border rounded-sm font-mono text-sm"
                        data-testid="mission-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono text-muted-foreground uppercase">Time</Label>
                      <Input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                        className="bg-input border-border rounded-sm font-mono text-sm"
                        data-testid="mission-time-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="mission-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-sm">
                        {priorityOptions.map(option => (
                          <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Destinations */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Destinations ({destinations.length})</Label>
                    
                    {/* Quick add locations */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {TANZANIA_LOCATIONS.slice(0, 4).map(loc => (
                        <Button
                          key={loc.name}
                          variant="outline"
                          size="sm"
                          onClick={() => addQuickLocation(loc)}
                          className="text-[10px] h-6 px-2 border-primary/30 text-primary hover:bg-primary/10"
                        >
                          + {loc.name.split(' ')[0]}
                        </Button>
                      ))}
                    </div>

                    {/* Destinations list */}
                    <ScrollArea className="h-32 border border-border rounded-sm p-2">
                      {destinations.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No destinations added</p>
                      ) : (
                        <div className="space-y-2">
                          {destinations.map((dest, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-accent/30 rounded-sm">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-primary/20 text-primary w-6 h-6 flex items-center justify-center rounded-full p-0">
                                  {index + 1}
                                </Badge>
                                <div>
                                  <p className="text-xs font-medium text-foreground">{dest.name}</p>
                                  <p className="text-[10px] font-mono text-muted-foreground">
                                    {dest.latitude.toFixed(4)}, {dest.longitude.toFixed(4)}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeDestination(index)}
                                className="h-6 w-6 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>

                    {/* Add custom destination */}
                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        placeholder="Name"
                        value={newDestination.name}
                        onChange={(e) => setNewDestination({ ...newDestination, name: e.target.value })}
                        className="bg-input border-border rounded-sm font-mono text-xs col-span-2"
                      />
                      <Input
                        placeholder="Lat"
                        value={newDestination.latitude}
                        onChange={(e) => setNewDestination({ ...newDestination, latitude: e.target.value })}
                        className="bg-input border-border rounded-sm font-mono text-xs"
                      />
                      <Input
                        placeholder="Lng"
                        value={newDestination.longitude}
                        onChange={(e) => setNewDestination({ ...newDestination, longitude: e.target.value })}
                        className="bg-input border-border rounded-sm font-mono text-xs"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addDestination}
                      className="w-full border-border text-foreground hover:bg-accent"
                    >
                      <MapPin className="w-3 h-3 mr-2" />
                      Add Destination
                    </Button>
                  </div>

                  {/* AI & Route Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleAIPlan}
                      disabled={aiLoading || destinations.length === 0}
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm font-mono text-xs"
                      data-testid="ai-plan-btn"
                    >
                      {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                      AI PLAN
                    </Button>
                    <Button
                      onClick={handleCalculateRoute}
                      disabled={routeLoading || destinations.length < 2}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-mono text-xs"
                      data-testid="calculate-route-btn"
                    >
                      {routeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Route className="w-4 h-4 mr-2" />}
                      CALC ROUTE
                    </Button>
                  </div>
                  <Button
                    onClick={handleOptimizeRoute}
                    disabled={aiLoading || destinations.length < 2}
                    variant="outline"
                    className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10 rounded-sm font-mono text-xs"
                    data-testid="optimize-route-btn"
                  >
                    {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
                    AI OPTIMIZE ROUTE ORDER
                  </Button>
                </div>

                {/* Right Column - Map & Results */}
                <div className="space-y-4">
                  {/* Map */}
                  <div className="h-48 rounded-sm overflow-hidden border border-border">
                    <MapContainer
                      center={defaultCenter}
                      zoom={6}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      />
                      <MapController destinations={destinations} />
                      {destinations.map((dest, index) => (
                        <Marker
                          key={index}
                          position={[dest.latitude, dest.longitude]}
                          icon={createDestinationIcon(index)}
                        >
                          <Popup>
                            <div className="text-xs">
                              <strong>{dest.name}</strong>
                              <br />
                              {dest.notes && <span>{dest.notes}</span>}
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                      {destinations.length >= 2 && (
                        <Polyline
                          positions={destinations.map(d => [d.latitude, d.longitude])}
                          color="#4ade80"
                          weight={3}
                          opacity={0.7}
                          dashArray="10, 10"
                        />
                      )}
                    </MapContainer>
                  </div>

                  {/* Route Info */}
                  {routeInfo && (
                    <Card className="bg-accent/30 border-primary/30 rounded-sm">
                      <CardContent className="p-3">
                        <p className="text-xs font-mono text-muted-foreground mb-2">ROUTE CALCULATED</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="text-center">
                            <Route className="w-4 h-4 mx-auto text-primary mb-1" />
                            <p className="text-lg font-heading font-bold text-foreground">{routeInfo.total_distance_km}</p>
                            <p className="text-[10px] font-mono text-muted-foreground">KM</p>
                          </div>
                          <div className="text-center">
                            <Clock className="w-4 h-4 mx-auto text-blue-400 mb-1" />
                            <p className="text-lg font-heading font-bold text-foreground">{routeInfo.estimated_duration_formatted}</p>
                            <p className="text-[10px] font-mono text-muted-foreground">DURATION</p>
                          </div>
                          <div className="text-center">
                            <Fuel className="w-4 h-4 mx-auto text-yellow-400 mb-1" />
                            <p className="text-lg font-heading font-bold text-foreground">{routeInfo.estimated_fuel_liters}</p>
                            <p className="text-[10px] font-mono text-muted-foreground">LITERS</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* AI Recommendation */}
                  {aiRecommendation && (
                    <Card className="bg-purple-500/10 border-purple-500/30 rounded-sm">
                      <CardHeader className="pb-2 pt-3 px-3">
                        <CardTitle className="text-xs font-mono text-purple-400 flex items-center gap-2">
                          <Brain className="w-4 h-4" />
                          AI RECOMMENDATION
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-3 pb-3">
                        <ScrollArea className="h-32">
                          <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
                            {aiRecommendation}
                          </pre>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Create Mission Button */}
                  <Button
                    onClick={handleCreateMission}
                    disabled={!formData.title || destinations.length === 0}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider"
                    data-testid="create-mission-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    CREATE MISSION
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-foreground">{missions.length}</p>
            <p className="text-xs font-mono text-muted-foreground">TOTAL MISSIONS</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-gray-400">{missions.filter(m => m.status === "planned").length}</p>
            <p className="text-xs font-mono text-muted-foreground">PLANNED</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-blue-400">{missions.filter(m => m.status === "in_progress").length}</p>
            <p className="text-xs font-mono text-muted-foreground">IN PROGRESS</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-green-400">{missions.filter(m => m.status === "completed").length}</p>
            <p className="text-xs font-mono text-muted-foreground">COMPLETED</p>
          </CardContent>
        </Card>
      </div>

      {/* Missions Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="missions-table">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="font-heading text-sm tracking-wide text-foreground flex items-center gap-2">
            <Navigation className="w-4 h-4 text-primary" />
            MISSIONS
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">MISSION</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DATE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DESTINATIONS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">PRIORITY</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">STATUS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">VEHICLE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {missions.map((mission) => {
                  const statusBadge = getStatusBadge(mission.status);
                  const priorityBadge = getPriorityBadge(mission.priority);
                  const vehicle = vehicles.find(v => v.id === mission.assigned_vehicle_id);
                  return (
                    <TableRow key={mission.id} className="border-border hover:bg-accent/50" data-testid={`mission-row-${mission.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{mission.title}</p>
                          <p className="text-xs font-mono text-muted-foreground truncate max-w-xs">{mission.description}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {mission.scheduled_date}
                        {mission.scheduled_time && <span className="text-xs"> {mission.scheduled_time}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-primary" />
                          <span className="font-mono text-sm text-foreground">{mission.destinations?.length || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${priorityBadge.className} rounded-sm font-mono text-xs border`}>
                          {priorityBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusBadge.className} rounded-sm font-mono text-xs border`}>
                          {statusBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {vehicle ? vehicle.registration_number : "Unassigned"}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {missions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground font-mono">
                      No missions planned yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
